import Sound from 'react-native-sound';

// Enable playback in silence mode on iOS
Sound.setCategory('Playback');

/**
 * AudioService — low-level audio playback via react-native-sound.
 *
 * @deprecated New code should use the `useAudioPlayback` hook instead of
 * calling AudioService directly. The hook wraps AudioService with proper
 * loading/playing/error state management and renders a visible fallback
 * when playback fails (rules/02 — no bare catches, no silent failures).
 * Direct calls to AudioService.play bypass error state management and
 * may result in silent failures if the caller doesn't handle rejections.
 */
export class AudioService {
    private static activeSound: Sound | null = null;
    private static isMuted: boolean = false;

    /**
     * Preloads an audio file
     */
    static preload(url: string): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.isMuted) return resolve(true);

            const _sound = new Sound(url, '', (error) => {
                if (error) {
                    console.warn('[AudioService] Failed to load sound:', url, error);
                    resolve(false);
                    return;
                }
                resolve(true);
            });
        });
    }

    /**
     * Plays an audio file from a URL or local path
     * Supports stopping currently playing audio so they don't overlap
     */
    static play(url: string, onComplete?: () => void): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isMuted) {
                onComplete?.();
                resolve();
                return;
            }

            this.stop(); // Stop any currently playing audio

            this.activeSound = new Sound(url, '', (error) => {
                if (error) {
                    console.error('[AudioService] Failed to load sound', url, error);
                    onComplete?.();
                    reject(error);
                    return;
                }

                // Play the sound
                this.activeSound?.play((success) => {
                    if (success) {
                        onComplete?.();
                        resolve();
                    } else {
                        console.error('[AudioService] Playback failed due to audio decoding errors');
                        onComplete?.();
                        reject(new Error('Playback failed'));
                    }
                    // Release when done
                    this.activeSound?.release();
                    this.activeSound = null;
                });
            });
        });
    }

    /**
     * Stops currently playing audio
     */
    static stop(): void {
        if (this.activeSound) {
            this.activeSound.stop(() => {
                this.activeSound?.release();
                this.activeSound = null;
            });
        }
    }

    /**
     * Mute all future playback
     */
    static setMuted(muted: boolean) {
        this.isMuted = muted;
        if (muted) {
            this.stop();
        }
    }
}
